import { useEffect, useState } from 'react';
import { WbFileInput, WbIcon, WbRoundButton } from '@workbench/react';
import { useTranslation } from 'react-i18next';

import _notification from '../../../Utils/Vimdt/notification';
import './ImageInput.scss';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];

/**
 * Single-image picker built on `wb-file-input`; reports the decoded data URL and
 * the intrinsic image dimensions back to the parent.
 */
function ImageInput({
	name,
	onImageSet,
	onImageDelete,
	disabled,
	oldImageUrl,
	fileSize,
	uploadText = 'Upload',
}) {
	const [imageUrl, setImageUrl] = useState(oldImageUrl);
	const { t } = useTranslation();

	useEffect(() => {
		setImageUrl(oldImageUrl || null);
	}, [oldImageUrl]);

	const deleteImage = (event) => {
		if (event) event.stopPropagation();
		setImageUrl(null);
		onImageDelete(name);
	};

	const handleChange = (event) => {
		const file = event.detail?.[0]?.file || event.target?.files?.[0];
		if (!file) return;

		if (file.size * 0.000001 >= fileSize) {
			_notification({
				message: t('vimdt.notification.loadedImageFileSizeError', { fileSize }),
				error: true,
			});
			return;
		}

		if (!ACCEPTED_TYPES.includes(file.type)) {
			_notification({
				message: t('vimdt.notification.loadedImageFileTypeError'),
				error: true,
			});
			return;
		}

		const reader = new FileReader();
		reader.addEventListener('load', () => {
			const url = reader.result;
			const image = new Image();
			image.addEventListener('load', () => {
				setImageUrl(url);
				onImageSet({ name, url, width: image.width, height: image.height, fileName: file.name });
			});
			image.src = url;
		});
		reader.readAsDataURL(file);
	};

	if (imageUrl) {
		return (
			<div className="vimdt-image-input__preview">
				<img src={imageUrl} alt={name} />
				<WbRoundButton
					size="s"
					aria-label={t('vimdt.components.deleteButton')}
					onClick={deleteImage}
				>
					<WbIcon name="bds/trash/24" />
				</WbRoundButton>
			</div>
		);
	}

	return (
		<WbFileInput
			name={name}
			size="s"
			accept={ACCEPTED_TYPES.join(',')}
			disabled={disabled || undefined}
			onWbchange={handleChange}
			data-testid={`image-input-${name}`}
		>
			<span slot="title">{uploadText}</span>
		</WbFileInput>
	);
}

export default ImageInput;
